import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation, Trans } from "react-i18next";
import { useWorkflowBiometricConsent } from "../../hooks/faceVerification/useWorkflowBiometricConsent";
import faceVerificationStyles from "../faceVerification/FaceVerification.module.scss";
import styles from "../faceVerification/slots/IProovSlots.module.scss";
import { logger } from "../../shared/utils/logger";

interface WorkflowIProovSlotsProps {
  consentEnabled?: boolean;
  biometricConsentApiBaseUrl?: string | null;
  onAcceptConsent?: () => Promise<void>;
  onConsentFailed?: (error: Error) => void;
  onConsentGetFailed?: (error: Error) => void;
}

/**
 * iProov slot templates for the Workflow Engine face verification flow.
 *
 * Mirror of IProovSlots but uses the BFF-based useWorkflowBiometricConsent hook
 * (session cookie + credentials: 'include') instead of direct API with sessionToken.
 * Kept separate to avoid coupling the IVA flow to WE-specific auth.
 */
export function WorkflowIProovSlots({
  consentEnabled,
  biometricConsentApiBaseUrl,
  onAcceptConsent,
  onConsentFailed,
  onConsentGetFailed,
}: WorkflowIProovSlotsProps) {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { error, consentText, fetchConsent, clearError } = useWorkflowBiometricConsent();

  const handleBiometricPolicyClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (!biometricConsentApiBaseUrl) return;

    try {
      await fetchConsent(biometricConsentApiBaseUrl);
      setIsModalOpen(true);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch consent');
      logger.error('[WorkflowIProovSlots] Consent policy fetch failed:', error.message);
      onConsentGetFailed?.(error);
    }
  };

  const handleOpenCameraClick = async () => {
    if (!consentEnabled || !onAcceptConsent) return;

    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Consent request timed out')), 3500)
    );

    try {
      await Promise.race([onAcceptConsent(), timeoutPromise]);
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Consent failed');
      logger.error('[WorkflowIProovSlots] Consent failed or timed out:', err.message);
      onConsentFailed?.(err);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    clearError();
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isModalOpen) handleCloseModal();
    };
    if (isModalOpen) document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isModalOpen]);

  return (
    <>
      {/* Ready slot */}
      <div slot="ready" className="ready-slot-container">
        <p className="heading-verification">{t('face.ready.heading')}</p>

        <div className="illustration-container">
          <img src="/assets/svg/selfie.svg" className="selfie-illustration" role="none" alt="" />
        </div>

        <div className="info-items-container">
          <div className="info-items">
            <div className="info-item">
              <img src="/assets/svg/icon-brightness.svg" className="info-icon" role="none" alt="" />
              <p className="info-text">{t('face.ready.info.brightness')}</p>
            </div>
            <div className="info-item">
              <img src="/assets/svg/icon-glasses.svg" className="info-icon" role="none" alt="" />
              <p className="info-text">{t('face.ready.info.glasses')}</p>
            </div>
            <div className="info-item">
              <img src="/assets/svg/icon-flash.svg" className="info-icon" role="none" alt="" />
              <p className="info-text">{t('face.ready.info.flash')} <a href="https://help.id.me/hc/en-us/articles/360061369314-How-to-take-an-ID-me-video-selfie" target="_blank" rel="noopener noreferrer" className="learn-more-link">{t('face.ready.learnMore')}</a></p>
            </div>
            <div className="info-item">
              <img src="/assets/svg/lock.svg" className="info-icon" role="none" alt="" />
              <p className="info-text">{t('face.ready.info.security')}</p>
            </div>
          </div>
        </div>

        {consentEnabled && (
          <div style={{ textAlign: 'left', padding: '0 24px 8px' }}>
            <span className={styles.consentText}>
              <Trans
                i18nKey="face.ready.consentText"
                components={[
                  <button
                    key="link"
                    type="button"
                    onClick={handleBiometricPolicyClick}
                    className={styles.consentLink}
                  />
                ]}
              />
            </span>
          </div>
        )}
      </div>

      {/* Button slot */}
      <div slot="button" className="button-slot-container" style={{ paddingTop: '8px' }}>
        <button type="button" className="btn btn-primary" onClick={handleOpenCameraClick}>
          {t('face.ready.button')}
        </button>
      </div>

      {/* Biometric Policy modal */}
      {consentEnabled && isModalOpen && createPortal(
        <div className={styles.modalOverlay} onClick={handleCloseModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{t('face.biometricPolicy.title')}</h2>
              <button onClick={handleCloseModal} className={styles.closeButton} aria-label="Close modal">×</button>
            </div>
            <div className={styles.modalBody}>
              {error ? (
                <p style={{ color: '#dc3545' }}>{error}</p>
              ) : consentText ? (
                <div dangerouslySetInnerHTML={{ __html: consentText }} />
              ) : (
                <p>Loading consent policy...</p>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Grant permission slot */}
      <div slot="grant_permission">
        <div className="grant-permission-icon">
          <img src="/assets/svg/camera-transparent.svg" width="48" height="48" role="none" alt="" />
        </div>
        <p className="grant-permission-heading">{t('face.grantPermission.heading')}</p>
        <p className="grant-permission-subtitle">{t('face.grantPermission.subtitle')}</p>
      </div>

      {/* Grant button slot */}
      <div slot="grant_button" className="grant-button-container">
        <button type="button" className="btn btn-primary">{t('face.grantPermission.button')}</button>
      </div>

      {/* Progress slot */}
      <div slot="progress">
        <div data-target="processing">
          <p className="heading">{t('face.progress.heading')}</p>
          <div className={faceVerificationStyles.spinner}></div>
        </div>
      </div>

      {/* Passed slot */}
      <div slot="passed">
        <div data-target="processing">
          <p className="heading">{t('face.passed.heading')}</p>
          <div className={faceVerificationStyles.spinner}></div>
        </div>
      </div>

      {/* Canceled slot */}
      <div slot="canceled">
        <p><img src="/assets/svg/warning.svg" className="icon-warning" role="none" alt="" /></p>
        <p className="heading iproov-lang-term"></p>
        <p className="iproov-lang-heading"></p>
        <button type="button" className="btn btn-primary">{t('face.canceled.button')}</button>
      </div>

      {/* Error slot */}
      <div slot="error">
        <p><img src="/assets/svg/warning.svg" className="icon-warning" role="none" alt="" /></p>
        <p className="heading iproov-lang-term"></p>
        <p className="iproov-lang-heading"></p>
        <button type="button" className="btn btn-primary">{t('face.error.button')}</button>
      </div>

      {/* Failed slot */}
      <div slot="failed">
        <p><img src="/assets/svg/warning.svg" className="icon-warning" role="none" alt="" /></p>
        <p className="heading iproov-lang-term"></p>
        <p className="iproov-lang-heading"></p>
        <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
          {t('face.failed.button')}
        </button>
      </div>

      {/* Permission denied slot */}
      <div slot="permission_denied">
        <p><img src="/assets/svg/warning.svg" className="icon-warning" role="none" alt="" /></p>
        <p className="heading">{t('face.permissionDenied.heading')}</p>
        <button type="button" className="btn btn-primary">{t('face.permissionDenied.button')}</button>
      </div>

      {/* No camera slot */}
      <div slot="no_camera">
        <p><img src="/assets/svg/warning.svg" className="icon-warning" role="none" alt="" /></p>
        <p className="heading iproov-lang-term"></p>
        <p className="iproov-lang-heading"></p>
      </div>

      {/* Unsupported slot */}
      <div slot="unsupported">
        <p><img src="/assets/svg/warning.svg" className="icon-warning" role="none" alt="" /></p>
        <p className="heading iproov-lang-term"></p>
        <p className="iproov-lang-heading"></p>
        <button type="button" className="btn btn-primary">Continue</button>
      </div>

      {/* Rotate portrait slot */}
      <div slot="rotate_portrait">
        <p className="heading">{t('face.rotatePortrait.heading')}</p>
      </div>
    </>
  );
}
